import * as React from 'react';

import {Alert, Box, Button, Card, CardContent, CircularProgress, Stack, Typography} from '@mui/material';
import {ChangeEvent, useEffect, useState} from 'react';
import {Title, useDataProvider, useNotify} from 'react-admin';

interface MapImageSettings {
  id: number;
  imageUrl: string | null;
  imageWidth: number | null;
  imageHeight: number | null;
}

interface ImageDimensions {
  width: number;
  height: number;
}

const SETTINGS_ID = 1;
const FALLBACK_DIMENSIONS: ImageDimensions = {width: 1090, height: 641};
const aspectRatio = (dimensions: ImageDimensions) => dimensions.width / dimensions.height;

export const MapSettingsPage = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [settings, setSettings] = useState<MapImageSettings>();
  const [selectedFile, setSelectedFile] = useState<File>();
  const [selectedDimensions, setSelectedDimensions] = useState<ImageDimensions>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let isMounted = true;

    dataProvider.getOne<MapImageSettings>('map-image', {id: SETTINGS_ID})
      .then(({data}) => {
        if (isMounted) {
          setSettings(data);
        }
      })
      .catch(() => {
        if (isMounted) {
          notify('Det gick inte att läsa in inställningarna för kartbilden.', {type: 'error'});
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [dataProvider, notify]);

  const selectImage = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const image = new Image();
    const imageUrl = URL.createObjectURL(file);
    image.onload = () => {
      setSelectedFile(file);
      setSelectedDimensions({width: image.naturalWidth, height: image.naturalHeight});
      URL.revokeObjectURL(imageUrl);
    };
    image.onerror = () => {
      URL.revokeObjectURL(imageUrl);
      notify('Den valda filen är inte en giltig bild.', {type: 'error'});
    };
    image.src = imageUrl;
  };

  const uploadImage = async () => {
    if (!selectedFile) {
      return;
    }

    setIsSaving(true);
    try {
      const {data} = await dataProvider.update<MapImageSettings>('map-image', {
        id: SETTINGS_ID,
        data: {image: {rawFile: selectedFile}},
        previousData: settings,
      });
      setSettings(data);
      setSelectedFile(undefined);
      setSelectedDimensions(undefined);
      notify('Kartbilden har uppdaterats.', {type: 'success'});
    } catch {
      notify('Det gick inte att uppdatera kartbilden.', {type: 'error'});
    } finally {
      setIsSaving(false);
    }
  };

  const currentDimensions = settings?.imageWidth && settings?.imageHeight
    ? {width: settings.imageWidth, height: settings.imageHeight}
    : FALLBACK_DIMENSIONS;
  const hasAspectRatioWarning = Boolean(
    selectedDimensions
    && currentDimensions
    && Math.abs(aspectRatio(selectedDimensions) - aspectRatio(currentDimensions)) > 0.01,
  );

  return (
    <Card>
      <Title title="Kartbild" />
      <CardContent>
        <Stack spacing={3} maxWidth={840}>
          <Box>
            <Typography variant="h5" gutterBottom>Kartbild</Typography>
            <Typography color="text.secondary">
              Befintliga koordinater för arbetstillstånd behålls när bilden ändras.
            </Typography>
          </Box>

          {isLoading ? <CircularProgress /> : (
            <>
              {settings?.imageUrl ? (
                <Box>
                  <Typography variant="subtitle1">Nuvarande bild</Typography>
                  <Box component="img" src={settings.imageUrl} alt="Nuvarande kartbild" sx={{display: 'block', maxWidth: '100%', maxHeight: 360, mt: 1}} />
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    {settings.imageWidth} × {settings.imageHeight}px
                  </Typography>
                </Box>
              ) : (
                <Alert severity="info">Den medföljande standardkartbilden används för närvarande.</Alert>
              )}

              <Button variant="outlined" component="label" disabled={isSaving}>
                Välj en PNG-, JPEG- eller WebP-bild
                <input hidden type="file" accept="image/png,image/jpeg,image/webp" onChange={selectImage} />
              </Button>

              {selectedFile && selectedDimensions && (
                <Box>
                  <Typography>{selectedFile.name} ({selectedDimensions.width} × {selectedDimensions.height}px)</Typography>
                  {hasAspectRatioWarning && (
                    <Alert severity="warning" sx={{mt: 1}}>
                      Den här bilden har ett annat bildförhållande. Befintliga markörer behåller sina normaliserade positioner, men kan sluta stämma överens med detaljer på kartan.
                    </Alert>
                  )}
                </Box>
              )}

              <Box>
                <Button variant="contained" onClick={uploadImage} disabled={!selectedFile || isSaving}>
                  {isSaving ? 'Laddar upp…' : 'Använd den här kartbilden'}
                </Button>
              </Box>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default MapSettingsPage;
